/**
 * Protocols — paid, deeper programmes that build on the free Foundations.
 *
 * Purchases are simulated in demo mode (an LNURL-pay style invoice is shown).
 * Content stays PRT-informed: tracking, not fixing.
 */

export interface ProtocolSession {
  id: string;
  title: string;
  durationSec: number;
  script: string;
}

export interface RoadmapItem {
  id: string;
  text: string;
}

export interface RoadmapTier {
  id: string;
  title: string;
  description: string;
  emoji: string;
  items: RoadmapItem[];
}

export type ProtocolKind = 'sessions' | 'roadmap' | 'bundle';

export interface Protocol {
  id: string;
  title: string;
  emoji: string;
  sessionCount: number;
  priceSats: number;
  description: string;
  hue: string;
  kind: ProtocolKind;
  /** For `bundle` protocols: the ids it unlocks. */
  includes?: string[];
  sessions?: ProtocolSession[];
  tiers?: RoadmapTier[];
}

const somaticFoundations: ProtocolSession[] = [
  {
    id: 'sf-body-scan',
    title: 'Body Scan',
    durationSec: 300,
    script:
      'Settle and let your attention travel slowly from head to feet. {pause} You are not fixing anything — just tracking. {pause} Tight, warm, heavy, quiet — every sensation belongs. {pause} When the mind wanders, that is allowed too. Come back without judgment.',
  },
  {
    id: 'sf-pendulation',
    title: 'Pendulation',
    durationSec: 300,
    script:
      'Find one place that feels neutral or okay, and rest there. {pause} Then, for a single breath, let your awareness touch the edge of something difficult. {pause} Return to the neutral place. {pause} Back and forth — this is pendulation. {pause} You are safe enough to look around, and small doses are enough.',
  },
  {
    id: 'sf-orienting',
    title: 'Orienting',
    durationSec: 300,
    script:
      'Slowly let your eyes move around the room. {pause} Notice edges, colours, light. {pause} Each look tells your nervous system: no threat here, right now. {pause} Nothing to fix. Just orienting.',
  },
  {
    id: 'sf-grounding',
    title: 'Grounding',
    durationSec: 300,
    script:
      'Feel the surface beneath you. {pause} Let it take your full weight. {pause} Notice your feet, your seat, your back. {pause} You can let the ground do some of the holding.',
  },
  {
    id: 'sf-resourcing',
    title: 'Resourcing',
    durationSec: 300,
    script:
      'Bring to mind a place, a person, or a memory that feels steady. {pause} Notice where that steadiness lands in your body. {pause} Let it be there. {pause} This resource belongs to you, and you can return to it whenever you need.',
  },
  {
    id: 'sf-titration',
    title: 'Titration',
    durationSec: 300,
    script:
      'We will touch difficulty in small doses, then step back. {pause} A little contact, then a return to comfort. {pause} You set the size. {pause} Small is not weak — small is how the body learns safety.',
  },
  {
    id: 'sf-integration',
    title: 'Integration',
    durationSec: 300,
    script:
      'Nothing new to learn here. {pause} Just notice what is different from when you began — even slightly. {pause} Let the body keep whatever it found. {pause} This is allowed.',
  },
];

const natureSessions: ProtocolSession[] = [
  {
    id: 'nr-tree',
    title: 'Meeting a Tree',
    durationSec: 300,
    script:
      'Go outside and find a tree. {pause} Stand near it, close enough to touch. {pause} Notice its bark, its roots, the way it holds still while everything moves. {pause} Silently ask it what it has witnessed. {pause} No gear, no goal — just company.',
  },
  {
    id: 'nr-forest-bathing',
    title: 'Forest Bathing',
    durationSec: 600,
    script:
      'Walk slowly among trees with no destination. {pause} Let the air, the green, and the quiet do the work. {pause} You are not exercising. {pause} You are letting a forest hold you for a while.',
  },
  {
    id: 'nr-earthing',
    title: 'Earthing',
    durationSec: 300,
    script:
      'Take off your shoes. {pause} Stand on grass, sand, or soil. {pause} Feel five points of contact on each foot. {pause} Let your weight drop into the ground. {pause} Simple contact — that is the whole practice.',
  },
  {
    id: 'nr-listening',
    title: 'Listening',
    durationSec: 240,
    script:
      'Find somewhere still, and close your eyes. {pause} Listen to the nearest sound, then the farthest. {pause} Let your attention travel between them. {pause} Do not name the sounds — just let them arrive.',
  },
  {
    id: 'nr-sky-gazing',
    title: 'Sky Gazing',
    durationSec: 300,
    script:
      'Lie back or stand, and watch the sky. {pause} Follow one cloud until it changes. {pause} Nothing you see is permanent — including the mood you arrived with.',
  },
  {
    id: 'nr-walking-meditation',
    title: 'Walking Meditation',
    durationSec: 600,
    script:
      'Walk slowly, matching breath to steps. {pause} Feel each foot meet the ground. {pause} When your mind races ahead, let the next step bring you back. {pause} Nowhere to arrive.',
  },
  {
    id: 'nr-sunset',
    title: 'Sunset Ritual',
    durationSec: 300,
    script:
      'Watch the light change as the day ends. {pause} Let it mark a boundary: what is done is done. {pause} Breathe out the day. {pause} You do not have to carry all of it into the evening.',
  },
];

const movementSessions: ProtocolSession[] = [
  {
    id: 'mg-qigong',
    title: 'Qigong',
    durationSec: 300,
    script:
      'Slow, deliberate movement with the breath. {pause} This is not exercise — it is gathering. {pause} Move as if you were pushing through water, and let each motion settle you.',
  },
  {
    id: 'mg-shaking',
    title: 'Shaking',
    durationSec: 180,
    script:
      'Shake out your hands, your arms, your legs, your whole body. {pause} Let it be loose and undignified. {pause} You are discharging, not performing.',
  },
  {
    id: 'mg-walking',
    title: 'Slow Walking',
    durationSec: 300,
    script:
      'Walk slower than feels normal. {pause} Let your feet feel the ground fully. {pause} This is not a walk to anywhere.',
  },
  {
    id: 'mg-stretching',
    title: 'Stretching',
    durationSec: 300,
    script:
      'Reach, lengthen, and yawn your way through the body. {pause} Stop before it hurts. {pause} You are making room, not achieving a shape.',
  },
  {
    id: 'mg-earthing',
    title: 'Earthing',
    durationSec: 180,
    script:
      'Stand barefoot on the earth. {pause} Sway gently, and feel the ground meet you exactly where you are.',
  },
  {
    id: 'mg-swaying',
    title: 'Swaying',
    durationSec: 180,
    script:
      'Stand and sway slowly side to side, like a tree in a light wind. {pause} Let your weight shift. {pause} Nothing to hold on to.',
  },
  {
    id: 'mg-flow',
    title: 'Flow',
    durationSec: 300,
    script:
      'Link the movements that felt good into one slow, continuous flow. {pause} No rules. {pause} Follow what your body asks for.',
  },
];

const nsdrSessions: ProtocolSession[] = [
  {
    id: 'nsdr-nsdr',
    title: 'NSDR',
    durationSec: 600,
    script:
      'Lie down. {pause} Let attention drift slowly through the body, intending only to rest. {pause} This is not sleep — it is deliberate rest while awake. {pause} When attention wanders, return gently.',
  },
  {
    id: 'nsdr-yoga-nidra',
    title: 'Yoga Nidra',
    durationSec: 900,
    script:
      'A long, guided rest. {pause} Rotate awareness through the body, then rest in the space between thoughts. {pause} You have nothing to do, and nowhere to be.',
  },
  {
    id: 'nsdr-sleep-hygiene',
    title: 'Sleep Hygiene',
    durationSec: 240,
    script:
      'A short check-in before bed: cool room, dark space, same time each night. {pause} Not rules to perfect — just signals that tell your body sleep is next.',
  },
  {
    id: 'nsdr-digital-sunset',
    title: 'Digital Sunset',
    durationSec: 180,
    script:
      'Set a time each evening when screens go dim and quiet. {pause} Not a punishment — a boundary. {pause} Let your eyes and mind soften before sleep.',
  },
  {
    id: 'nsdr-wind-down',
    title: 'Evening Wind-Down',
    durationSec: 300,
    script:
      'A slow sequence to close the day: dim light, long exhales, heavy limbs. {pause} Let the day finish instead of running on.',
  },
];

const digitalTiers: RoadmapTier[] = [
  {
    id: 'phone',
    title: 'Phone Hygiene',
    description: 'Quiet the device that owns your attention.',
    emoji: '📵',
    items: [
      { id: 'phone-1', text: 'Turn off all non-essential notifications' },
      { id: 'phone-2', text: 'Grayscale your home screen' },
      { id: 'phone-3', text: 'Install a FOSS keyboard (HeliBoard, FUTO, or similar)' },
      { id: 'phone-4', text: 'Delete one app that owns your attention' },
      { id: 'phone-5', text: 'Move your phone out of the bedroom' },
    ],
  },
  {
    id: 'privacy',
    title: 'Privacy Foundations',
    description: 'Close the doors you never knew were open.',
    emoji: '🛡️',
    items: [
      { id: 'privacy-1', text: 'Install Signal for private messaging' },
      { id: 'privacy-2', text: 'Create a Proton Mail address' },
      { id: 'privacy-3', text: 'Set up Bitwarden with unique passwords' },
      { id: 'privacy-4', text: 'Install Mullvad VPN' },
      { id: 'privacy-5', text: 'Turn on two-factor authentication everywhere' },
    ],
  },
  {
    id: 'social',
    title: 'Social Sovereignty',
    description: 'Own your identity and your audience.',
    emoji: '🌐',
    items: [
      { id: 'social-1', text: 'Create a Nostr identity (an npub)' },
      { id: 'social-2', text: 'Unfollow three accounts that agitate you' },
      { id: 'social-3', text: 'Remove Meta apps (Instagram, Facebook, WhatsApp)' },
      { id: 'social-4', text: 'Follow people directly instead of via an algorithm' },
      { id: 'social-5', text: 'Back up your nsec somewhere safe' },
    ],
  },
  {
    id: 'os',
    title: 'OS Freedom',
    description: 'Take the operating system back from the defaults.',
    emoji: '🧩',
    items: [
      { id: 'os-1', text: 'Dual-boot Linux on an old laptop' },
      { id: 'os-2', text: 'Install GrapheneOS on a supported phone' },
      { id: 'os-3', text: 'Replace Google Play with F-Droid' },
      { id: 'os-4', text: 'Use open-source apps for daily tasks' },
      { id: 'os-5', text: 'Learn five basic command-line commands' },
    ],
  },
  {
    id: 'custody',
    title: 'Self-Custody',
    description: 'Hold your own keys and your own money.',
    emoji: '🔑',
    items: [
      { id: 'custody-1', text: 'Buy a hardware wallet' },
      { id: 'custody-2', text: 'Verify your seed phrase backup' },
      { id: 'custody-3', text: 'Move Bitcoin off exchanges into self-custody' },
      { id: 'custody-4', text: 'Run your own Lightning node (or an NWC wallet)' },
      { id: 'custody-5', text: 'Verify your own transactions — don’t trust, verify' },
    ],
  },
];

export const PROTOCOLS: Protocol[] = [
  {
    id: 'somatic-foundations',
    title: 'Somatic Foundations',
    emoji: '🧘',
    sessionCount: 7,
    priceSats: 7000,
    description:
      'Seven PRT-informed sessions to build a steady relationship with your body — body scan, pendulation, orienting, grounding, resourcing, titration, and integration.',
    hue: 'from-violet-500/20 to-fuchsia-500/5',
    kind: 'sessions',
    sessions: somaticFoundations,
  },
  {
    id: 'digital-sovereignty',
    title: 'Digital Sovereignty',
    emoji: '🛡️',
    sessionCount: 25,
    priceSats: 5000,
    description:
      'A practical 25-step roadmap from turning off notifications to running your own Lightning node. No extremism — just boundaries you can actually keep.',
    hue: 'from-sky-500/20 to-cyan-500/5',
    kind: 'roadmap',
    tiers: digitalTiers,
  },
  {
    id: 'nature-reconnection',
    title: 'Nature Reconnection',
    emoji: '🌿',
    sessionCount: 7,
    priceSats: 5000,
    description:
      'Seven simple outdoor sessions: meeting a tree, forest bathing, earthing, listening, sky gazing, walking meditation, and a sunset ritual. No gear needed.',
    hue: 'from-emerald-500/20 to-lime-500/5',
    kind: 'sessions',
    sessions: natureSessions,
  },
  {
    id: 'movement-grounding',
    title: 'Movement as Grounding',
    emoji: '🕺',
    sessionCount: 7,
    priceSats: 5000,
    description:
      'Seven movement practices for regulation, not fitness: qigong, shaking, slow walking, stretching, earthing, swaying, and flow.',
    hue: 'from-amber-500/20 to-orange-500/5',
    kind: 'sessions',
    sessions: movementSessions,
  },
  {
    id: 'nsdr-sleep',
    title: 'NSDR & Sleep Protocol',
    emoji: '🌙',
    sessionCount: 5,
    priceSats: 4000,
    description:
      'Five rest-first sessions: non-sleep deep rest, yoga nidra, sleep hygiene, a digital sunset, and an evening wind-down. Deliberate rest, not sleep.',
    hue: 'from-indigo-500/20 to-blue-500/5',
    kind: 'sessions',
    sessions: nsdrSessions,
  },
  {
    id: 'all-protocols',
    title: 'All Protocols Bundle',
    emoji: '🎁',
    sessionCount: 49,
    priceSats: 20000,
    description:
      'Every protocol in one purchase — Somatic, Digital, Nature, Movement, and NSDR. Save 20% versus buying them separately.',
    hue: 'from-primary/25 to-primary/5',
    kind: 'bundle',
    includes: [
      'somatic-foundations',
      'digital-sovereignty',
      'nature-reconnection',
      'movement-grounding',
      'nsdr-sleep',
    ],
  },
];

export const BUNDLE_ID = 'all-protocols';

export function findProtocol(id: string): Protocol | undefined {
  return PROTOCOLS.find((protocol) => protocol.id === id);
}

/** True when `owned` grants access to `protocolId` (directly or via the bundle). */
export function ownsProtocol(owned: string[], protocolId: string): boolean {
  return owned.includes(protocolId) || owned.includes(BUNDLE_ID);
}

export const TOTAL_ROADMAP_ITEMS = digitalTiers.reduce(
  (sum, tier) => sum + tier.items.length,
  0,
);
